import { useApp } from '../contexts/AppContext';
import { useTr } from '../utils/useTr';

const TYPE = {
  match:           { color:'var(--gold)' },
  new_match:       { color:'var(--gold)' },
  vote:            { color:'var(--gold)' },
  friend_voted:    { color:'var(--gold)' },
  friend_rated:    { color:'var(--gold)' },
  like:            { color:'var(--muted)' },
  super_like:      { color:'var(--gold)' },
  message:         { color:'var(--blue, #6BA9D8)' },
  new_message:     { color:'var(--blue, #6BA9D8)' },
  scout:           { color:'var(--gold)' },
  scout_proposal:  { color:'var(--gold)' },
  scout_cancelled: { color:'var(--muted)' },
  circle:          { color:'var(--gold)' },
  circle_proposal: { color:'var(--gold)' },
  event:           { color:'var(--green, #4EC994)' },
  event_reminder:  { color:'var(--green, #4EC994)' },
  system:          { color:'var(--muted)' },
};

function timeAgo(iso, lang) {
  if (!iso) return '';
  const fr = lang === 'fr';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 1) return fr ? "à l'instant" : 'just now';
  if (m < 60) return fr ? `il y a ${m} min` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return fr ? `il y a ${h} h` : `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return fr ? `il y a ${d} j` : `${d}d ago`;
  return new Date(iso).toLocaleDateString(fr ? 'fr-FR' : 'en-US', { month:'short', day:'numeric' });
}

export default function Notifications() {
  const tr = useTr();
  const { notifications, setPage, markRead, markAllRead, lang } = useApp();
  const fr = lang === 'fr';

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    const data = n.data || {};
    // Feature B — rating reciprocity: jump straight to /#rate/<rater>
    if (data.action === 'rate_back' && data.rate_target_id) {
      import('../utils/navigate').then(({ navigate }) => navigate(`rate/${data.rate_target_id}`));
      return;
    }
    if (data.action === 'open_messages' || data.matchId || n.type === 'match' || n.type === 'new_match') {
      if ((n.type === 'match' || n.type === 'new_match') && (data.matchUserId || data.userId)) {
        sessionStorage.setItem('openMatchUserId', String(data.matchUserId || data.userId));
      }
      setPage(data.action === 'open_messages' ? 'messages' : 'matches'); return;
    }
    if (data.conversationId || n.type === 'message' || n.type === 'new_message') {
      setPage('messages'); return;
    }
    if (data.eventId || n.type === 'event' || n.type === 'event_reminder') {
      setPage('agenda'); return;
    }
    if (data.scoutId || n.type?.startsWith?.('scout')) {
      setPage('scout'); return;
    }
    if (data.circleId || n.type?.startsWith?.('circle')) {
      setPage('scout'); return;
    }
    if (data.action === 'complete_profile' || n.type === 'friend_rated' || n.type === 'vote' || n.type === 'friend_voted') {
      setPage('profile'); return;
    }
  };

  return (
    <div>
      <div style={{ padding:'1rem 1rem 0', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'0.8rem', marginBottom:'1.2rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.8rem' }}>
          <button onClick={()=>{ setPage('discover'); }} style={{ background:'none', border:'none', cursor:'pointer', padding:'0.2rem', display:'flex' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="1.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.5rem', fontWeight:400 }}>{tr('notifications.title')}</h2>
        </div>
        {notifications?.some(n => !n.read) && (
          <button onClick={markAllRead} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'0.72rem', color:'var(--muted)', fontFamily:'Outfit', textTransform:'uppercase', letterSpacing:'0.08em' }}>
            {fr ? 'Tout marquer lu' : 'Mark all read'}
          </button>
        )}
      </div>

      {(!notifications || notifications.length === 0) && (
        <div style={{ padding:'3rem 1.5rem', textAlign:'center', color:'var(--muted)' }}>
          <div style={{ fontSize:'2.4rem', marginBottom:'0.6rem' }}>🐝</div>
          <p style={{ fontSize:'0.9rem', fontFamily:"'Cormorant Garamond',serif", fontStyle:'italic', margin:0 }}>
            {fr ? 'Aucune notification pour le moment.' : 'No notifications yet.'}
          </p>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:'1px', background:'var(--border)' }}>
        {(notifications || []).map(n => {
          const c = (TYPE[n.type] || TYPE.like).color;
          const title = n.title || (n.textKey ? tr(n.textKey, n.textVars||{}) : n.text) || '';
          const body  = n.body  || n.sub || '';
          const when  = n.created_at ? timeAgo(n.created_at, lang) : (n.time || '');
          return (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              style={{
                display:'flex', alignItems:'flex-start', gap:'0.8rem', textAlign:'left',
                background: n.read ? 'var(--charcoal)' : 'var(--dark)',
                padding:'0.9rem 1rem',
                borderLeft: n.read ? '2px solid transparent' : `2px solid ${c}`,
                border:'none', cursor:'pointer', width:'100%', fontFamily:'inherit',
              }}>
              <div style={{ flex:1, minWidth:0 }}>
                {title && (
                  <p style={{ fontSize:'0.88rem', fontWeight:n.read?400:600, marginBottom: body ? '0.15rem' : 0, color:'var(--white)' }}>
                    {title}
                  </p>
                )}
                {body && (
                  <p style={{ fontSize:'0.78rem', color:'var(--muted)', lineHeight:1.5, margin:0 }}>{body}</p>
                )}
                {when && (
                  <p style={{ fontSize:'0.65rem', color:'var(--muted2, #666)', marginTop:'0.25rem', margin:0 }}>{when}</p>
                )}
              </div>
              {!n.read && <div style={{ width:6, height:6, borderRadius:'50%', background:c, marginTop:'0.35rem', flexShrink:0 }}/>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
